import axios from "axios";

/**
 * Single, shared API client. Every request from every page goes through this
 * file. If a URL ever needs to change, it changes ONCE here.
 *
 * URL-construction robustness:
 *  - Trailing slashes on REACT_APP_BACKEND_URL are stripped so we can never
 *    end up with `//api/…` or a trailing-slash 307 redirect (which some
 *    edge networks bounce to http:// and browsers then block as mixed
 *    content — the exact class of bug that caused a real user's 404).
 *  - The final URL is built with `new URL(path, base)` so bad env values
 *    fail loudly at startup rather than silently at request time.
 */
const RAW = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW.replace(/\/+$/, ""); // no trailing slashes
if (!BACKEND_URL) {
    console.error(
        "[MeowMD] REACT_APP_BACKEND_URL is empty — API calls will fail. " +
            "Set it in /app/frontend/.env and restart the frontend.",
    );
}
const API_BASE = `${BACKEND_URL}/api`;

// Freeze the resolved URLs at boot so any drift is visible in the console
// without needing dev tools open.
export const API_URLS = Object.freeze({
    phase2: `${API_BASE}/quiz/phase2/generate`,
    photo: `${API_BASE}/photo-check/analyze`,
    report: `${API_BASE}/report/generate`,
    leads: `${API_BASE}/leads`,
    chat: `${API_BASE}/chat`,
});
console.info("[MeowMD] API base:", API_BASE);

const client = axios.create({
    // Report generation can take 45s; give ourselves generous headroom.
    timeout: 90_000,
    // Explicit content-type for JSON POSTs (multipart overrides it per-call).
    headers: { "Content-Type": "application/json" },
});

/**
 * Any transport-level failure (network error, cross-origin block, upstream
 * 404 from a CDN etc.) is logged with the exact URL + status + a snippet of
 * the response body. Then re-throw so callers can decide UX.
 */
client.interceptors.response.use(
    (r) => r,
    (err) => {
        const cfg = err?.config || {};
        const status = err?.response?.status;
        const data = err?.response?.data;
        let snippet = "";
        if (typeof data === "string") {
            snippet = data.slice(0, 200);
        } else if (data) {
            try {
                snippet = JSON.stringify(data).slice(0, 200);
            } catch {
                /* ignore */
            }
        }
        console.error("[MeowMD API error]", {
            url: cfg.url,
            method: cfg.method,
            status,
            snippet,
            message: err.message,
        });
        return Promise.reject(err);
    },
);

// --------------------------------------------------------------------------
// Public API
// --------------------------------------------------------------------------
export async function generatePhase2(payload) {
    const { data } = await client.post(API_URLS.phase2, payload);
    return data;
}

export async function analyzePhoto({ cat, file }) {
    const form = new FormData();
    form.append("cat", JSON.stringify(cat));
    form.append("photo", file, file.name || "photo.jpg");
    const { data } = await client.post(API_URLS.photo, form, {
        // Let the browser set the multipart boundary automatically.
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function generateReport(payload) {
    const { data } = await client.post(API_URLS.report, payload);
    return data;
}

export async function createLead(payload) {
    const { data } = await client.post(API_URLS.leads, payload);
    return data;
}

/**
 * Pull the most user-friendly message we can from an axios error.
 * Handles all three shapes we see in the wild:
 *   1. FastAPI validated response  →  { detail: "…" }  OR  { detail: [{msg,…}] }
 *   2. Upstream/CDN HTML 404       →  data is a string, no `.detail`
 *   3. Network-level (no response) →  err.message only
 */
export function apiErrorMessage(err, fallback = "Something went wrong.") {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;

    const status = err?.response?.status;
    if (status) {
        // Response arrived but had no JSON body — almost always a CDN/ingress
        // page. Give the user something actionable, not axios's raw message.
        if (status === 404) {
            return (
                "The service didn't respond (404). Please retry in a moment. " +
                "If it keeps happening, your network may be blocking the " +
                "connection to the MeowMD backend."
            );
        }
        if (status === 429) return "Too many requests. Please wait a moment and try again.";
        if (status >= 500) return "The service is temporarily unavailable. Please retry.";
    }

    // No response at all — CORS / network / timeout.
    if (err?.code === "ECONNABORTED") return "Request timed out. Please try again.";
    if (err?.message === "Network Error") {
        return "Network error — please check your connection and try again.";
    }
    return err?.message || fallback;
}
